<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("app_catalog.promo_bundle_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin"
                        size="md"
                        type="neutral"
                        @click="openCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("app_catalog.btn_add_bundle") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col-md-3">
                            <base-input>
                                <el-select
                                    v-model="kindFilter"
                                    :placeholder="$t('app_catalog.promo_kind')"
                                    clearable
                                    @change="loadList"
                                    class="w-100"
                                >
                                    <el-option :label="$t('app_catalog.promo_kind_sku')" value="sku"></el-option>
                                    <el-option :label="$t('app_catalog.promo_kind_wms')" value="wms_product"></el-option>
                                </el-select>
                            </base-input>
                        </div>
                        <div class="col-md-3">
                            <base-input>
                                <el-select
                                    v-model="statusFilter"
                                    :placeholder="$t('status')"
                                    clearable
                                    @change="loadList"
                                    class="w-100"
                                >
                                    <el-option :label="$t('app_catalog.status_draft')" :value="0"></el-option>
                                    <el-option :label="$t('app_catalog.status_active')" :value="1"></el-option>
                                </el-select>
                            </base-input>
                        </div>
                        <div class="col-md-3">
                            <base-input>
                                <el-select
                                    v-model="marketFilter"
                                    :placeholder="$t('app_catalog.market_label')"
                                    clearable
                                    @change="loadList"
                                    class="w-100"
                                >
                                    <el-option :label="$t('app_catalog.market_filter_all')" value=""></el-option>
                                    <el-option :label="$t('app_catalog.market_domestic')" value="domestic"></el-option>
                                    <el-option :label="$t('app_catalog.market_international')" value="international"></el-option>
                                    <el-option :label="$t('app_catalog.market_both')" value="both"></el-option>
                                </el-select>
                            </base-input>
                        </div>
                        <!-- Issue #88: flash_only filter -->
                        <div class="col-md-12 mt-2">
                            <el-checkbox v-model="flashOnly" :true-label="1" :false-label="0" @change="loadList">
                                {{ $t("app_catalog.flash_only_filter") }}
                            </el-checkbox>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="list"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('app_catalog.no_data')"
                        @expand-change="onExpandChange"
                    >
                        <!-- The slots a customer picks from (spec §5). ONLY the
                             choices: no SKU, no price, no stock — a bundle's price
                             lives on the bundle and its stock on other people's
                             products, so printing either here would be a number
                             that looks authoritative and is not. Lazy, one GET per
                             row, cached. -->
                        <el-table-column type="expand">
                            <template slot-scope="{ row }">
                                <div class="px-4 py-2" v-loading="isDetailLoading(row.id)">
                                    <div v-if="detailError[row.id]" class="text-danger small">
                                        {{ $t("app_catalog.expand_load_failed") }}
                                    </div>
                                    <div
                                        v-else-if="!isDetailLoading(row.id) && optionRows(row).length === 0"
                                        class="text-muted small"
                                    >
                                        {{ $t("app_catalog.bundle_options_empty") }}
                                    </div>
                                    <div v-else>
                                        <div v-for="slot in optionRows(row)" :key="slot.key" class="mb-2">
                                            <!-- An unnamed slot still has to be
                                                 distinguishable from the next one,
                                                 hence the ordinal fallback. -->
                                            <strong>{{ slot.label || $t("app_catalog.slot") + " " + slot.index }}</strong>
                                            <span v-if="slot.options.length === 0" class="text-muted small ml-2">
                                                {{ $t("app_catalog.bundle_options_empty") }}
                                            </span>
                                            <el-tag
                                                v-for="(name, i) in slot.options"
                                                :key="slot.key + '-' + i"
                                                size="mini"
                                                class="ml-2 mb-1"
                                            >
                                                {{ name }}
                                            </el-tag>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="ID" prop="id" min-width="60px"></el-table-column>
                        <el-table-column :label="$t('name')" min-width="200px">
                            <template slot-scope="{ row }">
                                <!-- ⚡ before the name, replacing the flash column
                                     (spec §5). -->
                                <el-tooltip
                                    v-if="flashActive(row)"
                                    :content="$t('app_catalog.flash_active_tooltip')"
                                    placement="top"
                                >
                                    <i class="fa fa-bolt text-danger mr-1"></i>
                                </el-tooltip>
                                {{ row.name_th || row.name_en }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.promo_kind')" min-width="120px">
                            <template slot-scope="{ row }">
                                {{ row.kind === "sku" ? $t("app_catalog.promo_kind_sku") : $t("app_catalog.promo_kind_wms") }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_starts_at')" min-width="130px">
                            <template slot-scope="{ row }">{{ formatDate(row.starts_at) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_ends_at')" min-width="130px">
                            <template slot-scope="{ row }">{{ formatDate(row.ends_at) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.market_label')" min-width="110px">
                            <template slot-scope="{ row }">
                                <el-tag
                                    :type="row.market === 'international' ? '' : row.market === 'both' ? 'success' : 'info'"
                                    size="small"
                                >
                                    {{ $t("app_catalog.market_" + (row.market || "domestic")) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <!-- The flash COLUMN was replaced by the ⚡ next to the name
                             on 2026-10-03 (spec §5). -->
                        <!-- #654: cashback badge. Shown ONLY when this bundle sets
                             its own percentage; 0 means it follows the shop value,
                             and a badge on every row would say nothing. -->
                        <el-table-column :label="$t('cashback_settings.badge')" min-width="110px">
                            <template slot-scope="{ row }">
                                <el-tag v-if="cashbackBadge(row)" type="success" size="small">
                                    {{ cashbackBadge(row) }}%
                                </el-tag>
                            </template>
                        </el-table-column>
                        <!-- สถานะการขาย — the SAME badge the product list shows, from
                             the same helper, so "พร้อมขาย" cannot come to mean two
                             different things on two seller pages. A bundle has no
                             stock half: it is a recipe over other people's SKUs. -->
                        <el-table-column :label="$t('app_catalog.col_sale_status')" min-width="120px">
                            <template slot-scope="{ row }">
                                <el-tooltip
                                    v-if="saleStatus(row).hintKey"
                                    :content="$t(saleStatus(row).hintKey)"
                                    placement="top"
                                >
                                    <el-tag :type="saleStatus(row).type" size="small">
                                        {{ $t(saleStatus(row).labelKey) }}
                                    </el-tag>
                                </el-tooltip>
                                <el-tag v-else :type="saleStatus(row).type" size="small">
                                    {{ $t(saleStatus(row).labelKey) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <!-- เปิด-ปิด → PATCH /promotions/:id/status, and NOT the
                             editor's PUT: BOCatalogPromotionUpdate hard-deletes and
                             re-inserts slots, options and the gallery
                             (mdf-bo-api controllers/Catalog_promotion_bundle.go:398),
                             so a partial PUT from here would empty the bundle. -->
                        <el-table-column :label="$t('app_catalog.col_toggle')" min-width="90px">
                            <template slot-scope="{ row }">
                                <el-switch
                                    :value="row.status"
                                    :active-value="1"
                                    :inactive-value="0"
                                    :disabled="!isAdmin || !!row.$toggling"
                                    @change="v => onToggleStatus(row, v)"
                                ></el-switch>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="isAdmin" :label="$t('actions')" width="72" align="center">
                            <template slot-scope="{ row }">
                                <!-- Same kebab as the product list
                                     (pattern: src/views/ModernTrade/MtPriceTemplates.vue).
                                     No "ดูหน้าร้าน" item: a bundle has no PDP of its
                                     own on the storefront. -->
                                <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        class="btn-icon-only"
                                        :title="$t('actions')"
                                        @click.native.stop
                                    >
                                        <i class="fa fa-ellipsis-v"></i>
                                    </base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="edit">
                                            <i class="fa fa-edit text-info mr-2"></i>{{ $t("edit") }}
                                        </el-dropdown-item>
                                        <el-dropdown-item command="delete" divided>
                                            <span class="text-danger">
                                                <i class="fa fa-trash mr-2"></i>{{ $t("app_catalog.btn_delete") }}
                                            </span>
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <template slot="footer">
                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            background
                            layout="prev, pager, next"
                            :total="total"
                            :page-size="pageSize"
                            :current-page="page"
                            @current-change="onPageChange"
                        ></el-pagination>
                    </div>
                </template>
            </card>
        </div>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import {
    Select,
    Option,
    Table,
    TableColumn,
    Tag,
    Pagination,
    Checkbox,
    Tooltip,
    Switch,
    Dropdown,
    DropdownMenu,
    DropdownItem
} from "element-ui";
import { formatCashbackPercent } from "@/utils/cashbackConfig";
// 2026-10-03 revamp — shared with Products.vue so the badge and the ⚡ mean the
// same thing on both pages. Pure + unit-tested in tests/unit/sellerProductRow.spec.js.
import { saleStatusOf, hasActiveFlash, bundleOptionRowsOf } from "@/utils/sellerProductRow";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import refreshOnArrival from "@/mixins/refreshOnArrival";
import { consumeRefresh } from "@/utils/pageRefresh";

export default {
    name: "AppCatalogPromotionBundles",
    mixins: [refreshOnArrival],
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination,
        [Checkbox.name]: Checkbox,
        [Tooltip.name]: Tooltip,
        [Switch.name]: Switch,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem
    },
    data() {
        return {
            kindFilter: "",
            statusFilter: "",
            marketFilter: "",
            flashOnly: 0,
            page: 1,
            pageSize: 20,
            // ---- 2026-10-03 revamp: the slot drill-down cache (see Products.vue) ----
            detailById: {},
            detailLoading: {},
            detailError: {}
        };
    },
    computed: {
        ...mapGetters({
            list: "AppCatalogPromotionBundle/list",
            total: "AppCatalogPromotionBundle/total",
            loading: "AppCatalogPromotionBundle/loading",
            isAdmin: "Auth/isAdmin"
        })
    },
    mounted() {
        // Fresh mount is already fresh: drop a stale flag so the next activation does not refetch.
        consumeRefresh(this.$route.fullPath);
        this.loadList();
    },
    methods: {
        // refreshOnArrival mixin: a sender asked for a refresh before navigating here.
        reloadOnArrival() {
            this.loadList();
        },
        ...mapActions({
            fetchList: "AppCatalogPromotionBundle/fetchList",
            removeItem: "AppCatalogPromotionBundle/remove",
            fetchDetail: "AppCatalogPromotionBundle/fetchDetail",
            setStatus: "AppCatalogPromotionBundle/setStatus"
        }),
        // ---- 2026-10-03 revamp: สถานะการขาย + ⚡ + เปิด-ปิด + slot drill-down ----
        saleStatus(row) {
            return saleStatusOf(row);
        },
        flashActive(row) {
            return hasActiveFlash(row);
        },
        isDetailLoading(id) {
            return !!this.detailLoading[id];
        },
        optionRows(row) {
            return bundleOptionRowsOf(this.detailById[row && row.id]);
        },
        onExpandChange(row, expanded) {
            const isOpen = Array.isArray(expanded)
                ? expanded.some(r => r && r.id === row.id)
                : !!expanded;
            if (!isOpen) return;
            this.loadDetail(row.id);
        },
        async loadDetail(id) {
            if (!id) return;
            if (this.detailById[id] || this.detailLoading[id]) return;
            this.$set(this.detailLoading, id, true);
            this.$set(this.detailError, id, false);
            try {
                const detail = await this.fetchDetail(id);
                this.$set(this.detailById, id, detail || {});
            } catch (err) {
                this.$set(this.detailError, id, true);
            } finally {
                this.$set(this.detailLoading, id, false);
            }
        },
        // Optimistic, same contract as the product switch: move first, revert on
        // failure, never reload the list.
        async onToggleStatus(row, value) {
            if (!this.isAdmin || row.$toggling) return;
            const previous = row.status;
            const next = Number(value) === 1 ? 1 : 0;
            this.$set(row, "status", next);
            this.$set(row, "$toggling", true);
            try {
                const res = await this.setStatus({ id: row.id, status: next });
                const body = (res && res.data && res.data.data) || (res && res.data) || {};
                if (body.status !== undefined && body.status !== null) {
                    this.$set(row, "status", Number(body.status) === 1 ? 1 : 0);
                }
                this.$notify({
                    type: "success",
                    message: this.$t(next === 1 ? "app_catalog.toggle_on_success" : "app_catalog.toggle_off_success")
                });
            } catch (err) {
                this.$set(row, "status", previous);
                this.$notify({ type: "danger", message: this.$t("app_catalog.toggle_failed") });
            } finally {
                this.$set(row, "$toggling", false);
            }
        },
        onRowCommand(cmd, row) {
            if (cmd === "edit") return this.openEdit(row);
            if (cmd === "delete") return this.confirmDelete(row);
        },
        // #654: the text for the cashback badge, or "" when this bundle has no
        // override (0, missing, or a value an un-migrated API could not answer).
        // Returning "" rather than "0" is what keeps the badge off every row
        // that simply follows the shop default.
        cashbackBadge(row) {
            const pct = formatCashbackPercent(row && row.cashback_percent);
            return pct === "" || Number(pct) <= 0 ? "" : pct;
        },
        loadList() {
            this.fetchList({
                kind: this.kindFilter,
                status: this.statusFilter === "" ? "" : this.statusFilter,
                market: this.marketFilter,
                flash_only: this.flashOnly || 0,
                page: this.page,
                limit: this.pageSize
            });
        },
        onPageChange(p) {
            this.page = p;
            this.loadList();
        },
        openCreate() {
            this.$router.push("/seller/bundles/new/edit");
        },
        openEdit(row) {
            this.$router.push(`/seller/bundles/${row.id}/edit`);
        },
        confirmDelete(row) {
            swal.fire({
                title: this.$t("app_catalog.delete_title"),
                text: this.$t("app_catalog.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("app_catalog.delete_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        },
        formatDate(dateStr) {
            if (!dateStr) return "-";
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return "-";
            const pad = n => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
    }
};
</script>
